import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FaUserEdit } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { updateMyProfileAPI } from "../../auth/apiUserProfile";
import { elakayAutoText } from "../../pageInside/dataElakay";
import { profileErrorMessage } from "./profileErrors";
import "../../pageInside/profileAddressPhoneModals.css";
import "./profileIdentity.css";

const NAME_MAX_LENGTH = 80;

// "Edit Full Name" (My Profile): first + last name only. The email is
// deliberately not part of this form -- the backend refuses it too.
const EditFullNameModal = ({ show, firstname = "", lastname = "", onHide, onSaved }) => {
  const [values, setValues] = useState({ firstname, lastname });
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Prefilled with the values currently stored, every time it opens.
  useEffect(() => {
    if (!show) return;
    setValues({ firstname: firstname || "", lastname: lastname || "" });
    setTouched({});
    setError("");
  }, [show, firstname, lastname]);

  const fieldError = (field) => {
    const value = values[field].trim();
    if (!value) {
      return field === "firstname"
        ? elakayAutoText("h_prf_firstNameRequired", "First name is required.")
        : elakayAutoText("h_prf_lastNameRequired", "Last name is required.");
    }
    if (value.length > NAME_MAX_LENGTH) return elakayAutoText("h_prf_nameTooLong", "Names can have at most 80 characters.");
    return "";
  };
  const errors = { firstname: fieldError("firstname"), lastname: fieldError("lastname") };
  const hasErrors = Boolean(errors.firstname || errors.lastname);
  const unchanged = values.firstname.trim() === (firstname || "").trim() && values.lastname.trim() === (lastname || "").trim();

  const change = (field) => (event) => {
    const { value } = event.target;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setTouched({ firstname: true, lastname: true });
    if (hasErrors || saving) return;
    setSaving(true);
    setError("");
    try {
      const result = await updateMyProfileAPI({ firstname: values.firstname.trim(), lastname: values.lastname.trim() });
      await onSaved?.(result.user);
    } catch (e) {
      setError(profileErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field, label) => {
    const showError = touched[field] && errors[field];
    return (
      <div className="epm-field">
        <label htmlFor={`prf-${field}`}>{label}</label>
        <input
          id={`prf-${field}`}
          className="form-control"
          value={values[field]}
          onChange={change(field)}
          onBlur={() => setTouched((prev) => ({ ...prev, [field]: true }))}
          maxLength={NAME_MAX_LENGTH + 20}
          autoComplete={field === "firstname" ? "given-name" : "family-name"}
          aria-invalid={showError ? "true" : "false"}
          aria-describedby={showError ? `prf-${field}-error` : undefined}
          disabled={saving}
          required
        />
        {showError && <p id={`prf-${field}-error`} className="epm-field-error">{errors[field]}</p>}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={() => !saving && onHide?.()}
      dialogClassName="epm-modal-dialog epm-modal-dialog--name"
      contentClassName="epm-modal-content"
      aria-labelledby="prf-edit-name-title"
      centered
    >
      <div className="epm-modal-header">
        <span className="epm-modal-icon"><FaUserEdit /></span>
        <div className="epm-modal-heading">
          <h5 id="prf-edit-name-title" className="epm-modal-title">{elakayAutoText("h_prf_editName_title", "Edit Full Name")}</h5>
          <p className="epm-modal-subtitle">{elakayAutoText("h_prf_editName_subtitle", "Your email address cannot be changed here.")}</p>
        </div>
        <button type="button" className="epm-modal-close" onClick={() => !saving && onHide?.()} aria-label={elakayAutoText("h_epm_closeModal", "Close")}>
          <MdClose />
        </button>
      </div>
      <Modal.Body className="epm-modal-body">
        <form onSubmit={submit} noValidate>
          {error && <div className="epm-alert-danger" role="alert">{error}</div>}
          <div className="epm-grid">
            {renderField("firstname", elakayAutoText("h_prf_firstName", "First Name"))}
            {renderField("lastname", elakayAutoText("h_prf_lastName", "Last Name"))}
          </div>
          <div className="epm-actions">
            <button type="button" className="epm-btn epm-btn-cancel" onClick={onHide} disabled={saving}>
              {elakayAutoText("h_prf_cancel", "Cancel")}
            </button>
            <button type="submit" className="epm-btn epm-btn-primary" disabled={saving || unchanged}>
              {saving ? elakayAutoText("h_prf_saving", "Saving…") : elakayAutoText("h_prf_saveChanges", "Save Changes")}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default EditFullNameModal;
